import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext as useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/Toast';
import { Header } from '../components/Header';
import { permissionChecks } from '../lib/auth/helpers';

import { 
  AssetsManagement, 
  AssetGroupsManagement,
  ChangeLaneAssignmentModal, 
  CheckpointsManagement, 
  LanesManagement, 
  LaneTypesManagement, 
  PlaceholderManagement, 
  SectionsManagement, 
  TerminalsManagement, 
  UsersManagement 
} from '../components/admin';


type AdminSection = 'users' | 'terminals' | 'checkpoints' | 'laneTypes' | 'laneAssignment' | 'sections' | 'assets' | 'assetGroups';

export const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState<AdminSection>('terminals');
  const { ToastContainer } = useToast();

          // Filter sidebar items based on user permissions
        const getFilteredSidebarItems = () => {
          const terminalsPermission = permissionChecks.canViewTerminals();
          const checkpointsPermission = permissionChecks.canViewCheckpoints();
          const queueTypesPermission = permissionChecks.canViewQueueTypes();
          const queueAssignmentPermission = permissionChecks.canViewQueueAssignments();
          const sectionsPermission = permissionChecks.canViewSections();
          const assetsPermission = permissionChecks.canViewAssets();
          const assetGroupsPermission = permissionChecks.canViewAssetGroups();

          // Debug logging removed for production



          const allItems = [
            { id: 'terminals' as AdminSection, label: 'Terminals', description: 'Add, edit, and manage terminals', permission: terminalsPermission },
            { id: 'checkpoints' as AdminSection, label: 'Checkpoints', description: 'Configure checkpoint settings', permission: checkpointsPermission },
            { id: 'laneTypes' as AdminSection, label: 'Queue Types', description: 'Manage queue types and configurations', permission: queueTypesPermission },
            { id: 'laneAssignment' as AdminSection, label: 'Queue Assignment', description: 'Manage Queue assignments and configurations', permission: queueAssignmentPermission },
            { id: 'sections' as AdminSection, label: 'Sections', description: 'Organize terminal sections', permission: sectionsPermission },
            { id: 'assets' as AdminSection, label: 'Assets', description: 'Manage system assets and resources', permission: assetsPermission },
            { id: 'assetGroups' as AdminSection, label: 'Asset Groups', description: 'Manage asset groups and assignments', permission: assetGroupsPermission }
          ];
    
    return allItems.filter(item => item.permission);
  };

  const sidebarItems = getFilteredSidebarItems();

  // Set initial active section based on available permissions
  useEffect(() => {
    if (sidebarItems.length > 0 && !sidebarItems.find(item => item.id === activeSection)) {
      setActiveSection(sidebarItems[0].id);
    }
  }, [sidebarItems, activeSection]);

  // Note: Authentication and admin access are now handled by ProtectedRoute in App.tsx
  // This component will only render if user is authenticated and has admin access

  const renderContent = () => {
    // If no sections are available, show no access message
    if (sidebarItems.length === 0) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-4">No Admin Access</h1>
            <p className="text-gray-400">You don't have permission to access any admin sections.</p>
          </div>
        </div>
      );
    }

    switch (activeSection) {
      case 'users':
        return <UsersManagement />;
      case 'terminals':
        return <TerminalsManagement />;
      case 'checkpoints':
        return <CheckpointsManagement />;
      case 'laneTypes':
        return <LaneTypesManagement />;
      case 'laneAssignment':
        return <LanesManagement />;
      case 'sections':
        return <SectionsManagement />;
      case 'assets':
        return <AssetsManagement />;
      case 'assetGroups':
        return <AssetGroupsManagement />;
      default:
        // Set active section to first available section if current one is not available
        if (sidebarItems.length > 0 && !sidebarItems.find(item => item.id === activeSection)) {
          setActiveSection(sidebarItems[0].id);
          return null;
        }
        return (
          <PlaceholderManagement 
            sectionName="Management" 
            description="Select a section from the sidebar" 
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
              <Header />
      <ToastContainer />
      
      <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)]">
        {/* Mobile-first Sidebar */}
        <aside className="w-full lg:w-64 bg-gray-800 border-b lg:border-r lg:border-b-0 border-gray-700 lg:h-full lg:overflow-y-auto scrollbar-hide">
          <div className="p-4">
            <h2 className="text-lg font-semibold mb-4 lg:mb-6">Admin Panel</h2>
            
            {/* Mobile: Horizontal scroll, Desktop: Vertical stack */}
            <nav className="flex lg:flex-col space-x-2 lg:space-x-0 lg:space-y-2 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0">
              {sidebarItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`flex-shrink-0 lg:flex-shrink text-left p-3 rounded-lg transition-colors duration-200 min-w-max lg:min-w-0 lg:w-full ${
                    activeSection === item.id ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="hidden sm:block lg:block">
                      <div className="font-medium">{item.label}</div>
                      <div className="text-xs text-gray-400 mt-1 hidden lg:block">{item.description}</div>
                    </div>
                    <div className="block sm:hidden lg:hidden">
                      <div className="font-medium text-sm">{item.label}</div>
                    </div>
                  </div>
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 p-4 lg:p-6 lg:overflow-y-auto scrollbar-hide">
          <div className="bg-gray-800 rounded-lg p-6">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};
